// SPDX-License-Identifier: AGPL-3.0-or-later

import {recordVoiceDiagnostic} from '@app/features/voice/diagnostics/VoiceDiagnostics';

const INSTALL_FLAG = '__fluxerVoiceDiagnosticsInstrumentationInstalled';
const RECURSION_FLAG = '__fluxerVoiceDiagnosticsInsideLog';

interface GlobalWithFlags {
	[INSTALL_FLAG]?: boolean;
	[RECURSION_FLAG]?: boolean;
}

function getGlobalFlags(): GlobalWithFlags {
	return globalThis as unknown as GlobalWithFlags;
}

function safeRecord(
	component: string,
	event: string,
	data?: Record<string, unknown>,
	level: 'debug' | 'info' | 'warn' | 'error' = 'info',
): void {
	const flags = getGlobalFlags();
	if (flags[RECURSION_FLAG]) return;
	flags[RECURSION_FLAG] = true;
	try {
		recordVoiceDiagnostic(component, event, data, level);
	} catch {
	} finally {
		flags[RECURSION_FLAG] = false;
	}
}

function summarizeArg(value: unknown, depth = 0): unknown {
	if (value == null) return value;
	if (value instanceof Error) {
		return {name: value.name, message: value.message, stack: value.stack};
	}
	const t = typeof value;
	if (t === 'string' || t === 'number' || t === 'boolean' || t === 'bigint') {
		return t === 'bigint' ? String(value) : value;
	}
	if (t === 'function' || t === 'symbol' || t === 'undefined') {
		return String(value);
	}
	if (depth > 2) return '<max-depth>';
	if (Array.isArray(value)) {
		return value.slice(0, 16).map((entry) => summarizeArg(entry, depth + 1));
	}
	try {
		const out: Record<string, unknown> = {};
		const entries = Object.entries(value as Record<string, unknown>).slice(0, 16);
		for (const [key, v] of entries) {
			out[key] = summarizeArg(v, depth + 1);
		}
		return out;
	} catch {
		try {
			return String(value);
		} catch {
			return '<unserializable>';
		}
	}
}

export interface ErrorBucket {
	signature: string;
	level: 'error' | 'warn';
	source: string;
	name?: string;
	message: string;
	stackHead?: string;
	count: number;
	firstSeenAt: number;
	lastSeenAt: number;
}

const MAX_ERROR_BUCKETS = 50;
const errorBuckets = new Map<string, ErrorBucket>();

function fingerprintError(
	name: string | undefined,
	message: string | undefined,
	stack: string | undefined,
): {
	signature: string;
	stackHead?: string;
} {
	const nameKey = name ?? 'Error';
	const msgKey = (message ?? '').slice(0, 200);
	let stackHead: string | undefined;
	if (stack) {
		for (const line of stack.split('\n')) {
			const trimmed = line.trim();
			if (trimmed?.startsWith('at ')) {
				stackHead = trimmed;
				break;
			}
		}
	}
	return {signature: `${nameKey}::${msgKey}::${stackHead ?? ''}`, stackHead};
}

function recordErrorOccurrence(
	source: string,
	level: 'error' | 'warn',
	name: string | undefined,
	message: string | undefined,
	stack: string | undefined,
): void {
	const {signature, stackHead} = fingerprintError(name, message, stack);
	const now = Date.now();
	let bucket = errorBuckets.get(signature);
	if (!bucket) {
		if (errorBuckets.size >= MAX_ERROR_BUCKETS) {
			let oldestKey: string | null = null;
			let oldestSeen = Number.POSITIVE_INFINITY;
			for (const [key, value] of errorBuckets) {
				if (value.lastSeenAt < oldestSeen) {
					oldestSeen = value.lastSeenAt;
					oldestKey = key;
				}
			}
			if (oldestKey !== null) errorBuckets.delete(oldestKey);
		}
		bucket = {
			signature,
			level,
			source,
			name,
			message: message ?? '',
			stackHead,
			count: 0,
			firstSeenAt: now,
			lastSeenAt: now,
		};
		errorBuckets.set(signature, bucket);
	}
	bucket.count += 1;
	bucket.lastSeenAt = now;
	bucket.level = level;
}

export function getErrorAggregates(): Array<ErrorBucket> {
	return Array.from(errorBuckets.values()).sort((a, b) => b.lastSeenAt - a.lastSeenAt);
}

async function hashDeviceId(value: string | null | undefined): Promise<string | null> {
	if (!value) return null;
	try {
		const data = new TextEncoder().encode(value);
		const digest = await crypto.subtle.digest('SHA-256', data);
		return Array.from(new Uint8Array(digest))
			.map((byte) => byte.toString(16).padStart(2, '0'))
			.join('');
	} catch {
		let hash = 0;
		for (let i = 0; i < value.length; i += 1) {
			hash = (hash << 5) - hash + value.charCodeAt(i);
			hash |= 0;
		}
		return `fallback-${Math.abs(hash).toString(16)}`;
	}
}

function installGlobalErrorHandlers(): void {
	if (typeof window === 'undefined') return;

	window.addEventListener(
		'error',
		(event) => {
			const target = event.target as (HTMLElement & {tagName?: string; src?: string; href?: string}) | null;
			const isResource = Boolean(target && target !== (window as unknown) && target.tagName);
			if (!isResource && typeof event.message === 'string' && event.message.startsWith('ResizeObserver loop')) {
				return;
			}
			const errorObj = event.error instanceof Error ? event.error : null;
			if (!isResource) {
				recordErrorOccurrence(
					'window.error',
					'error',
					errorObj?.name,
					errorObj?.message ?? (typeof event.message === 'string' ? event.message : undefined),
					errorObj?.stack,
				);
			}
			safeRecord(
				'window',
				isResource ? 'resource_error' : 'script_error',
				{
					message: event.message,
					filename: event.filename,
					lineno: event.lineno,
					colno: event.colno,
					error: errorObj
						? {name: errorObj.name, message: errorObj.message, stack: errorObj.stack}
						: summarizeArg(event.error),
					resource:
						isResource && target ? {tag: target.tagName, src: target.src ?? null, href: target.href ?? null} : null,
				},
				'error',
			);
		},
		true,
	);

	window.addEventListener('unhandledrejection', (event) => {
		const reason = event.reason;
		const reasonObj = reason instanceof Error ? reason : null;
		recordErrorOccurrence(
			'window.unhandledrejection',
			'error',
			reasonObj?.name,
			reasonObj?.message ?? (typeof reason === 'string' ? reason : undefined),
			reasonObj?.stack,
		);
		safeRecord(
			'window',
			'unhandled_rejection',
			{
				reason: reasonObj
					? {name: reasonObj.name, message: reasonObj.message, stack: reasonObj.stack}
					: summarizeArg(reason),
			},
			'error',
		);
	});
}

function installConsoleHooks(): void {
	if (typeof console === 'undefined') return;

	const originalError = console.error.bind(console);
	const originalWarn = console.warn.bind(console);

	console.error = (...args: Array<unknown>) => {
		try {
			safeRecord('console', 'error', {args: args.map((a) => summarizeArg(a))}, 'error');
		} catch {}
		originalError(...args);
	};
	console.warn = (...args: Array<unknown>) => {
		try {
			safeRecord('console', 'warn', {args: args.map((a) => summarizeArg(a))}, 'warn');
		} catch {}
		originalWarn(...args);
	};
}

function installMediaDeviceWatcher(): void {
	if (typeof navigator === 'undefined' || !navigator.mediaDevices) return;

	const enumerateAndLog = async (event: string): Promise<void> => {
		try {
			const devices = await navigator.mediaDevices.enumerateDevices();
			const summarized = await Promise.all(
				devices.map(async (device) => ({
					kind: device.kind,
					hasLabel: Boolean(device.label),
					deviceIdHash: await hashDeviceId(device.deviceId),
					groupIdHash: await hashDeviceId(device.groupId),
					isDefault: device.deviceId === 'default',
				})),
			);
			safeRecord('mediaDevices', event, {count: devices.length, devices: summarized});
		} catch (error) {
			safeRecord('mediaDevices', `${event}_error`, {error: summarizeArg(error)}, 'warn');
		}
	};

	void enumerateAndLog('initial_enumeration');

	navigator.mediaDevices.addEventListener('devicechange', () => {
		void enumerateAndLog('devicechange');
	});
}

function installGetUserMediaWrappers(): void {
	if (typeof navigator === 'undefined' || !navigator.mediaDevices) return;

	const md = navigator.mediaDevices as MediaDevices & {
		__fluxerWrappedGUM?: boolean;
	};
	if (md.__fluxerWrappedGUM) return;
	md.__fluxerWrappedGUM = true;

	const originalGUM = md.getUserMedia ? md.getUserMedia.bind(md) : null;
	if (originalGUM) {
		md.getUserMedia = async (constraints?: MediaStreamConstraints) => {
			const startedAt = performance.now();
			try {
				const stream = await originalGUM(constraints);
				safeRecord('mediaDevices', 'getUserMedia.success', {
					constraints: summarizeArg(constraints),
					durationMs: Math.round(performance.now() - startedAt),
					trackKinds: stream.getTracks().map((t) => ({kind: t.kind, label: t.label ? '<present>' : ''})),
				});
				return stream;
			} catch (error) {
				safeRecord(
					'mediaDevices',
					'getUserMedia.error',
					{
						constraints: summarizeArg(constraints),
						durationMs: Math.round(performance.now() - startedAt),
						error: summarizeArg(error),
					},
					'error',
				);
				throw error;
			}
		};
	}

	const originalGDM = md.getDisplayMedia ? md.getDisplayMedia.bind(md) : null;
	if (originalGDM) {
		md.getDisplayMedia = async (constraints?: DisplayMediaStreamOptions) => {
			const startedAt = performance.now();
			try {
				const stream = await originalGDM(constraints);
				safeRecord('mediaDevices', 'getDisplayMedia.success', {
					constraints: summarizeArg(constraints),
					durationMs: Math.round(performance.now() - startedAt),
					trackKinds: stream.getTracks().map((t) => ({kind: t.kind})),
				});
				return stream;
			} catch (error) {
				safeRecord(
					'mediaDevices',
					'getDisplayMedia.error',
					{
						constraints: summarizeArg(constraints),
						durationMs: Math.round(performance.now() - startedAt),
						error: summarizeArg(error),
					},
					'error',
				);
				throw error;
			}
		};
	}
}

function installPeerConnectionWatcher(): void {
	if (typeof window === 'undefined' || typeof RTCPeerConnection === 'undefined') return;

	const wrappedFlag = '__fluxerWrappedPC';
	const ctor = window.RTCPeerConnection as unknown as {[key: string]: unknown};
	if (ctor[wrappedFlag]) return;
	ctor[wrappedFlag] = true;

	const Original = window.RTCPeerConnection;
	const Wrapped = function FluxerInstrumentedPeerConnection(
		...args: ConstructorParameters<typeof RTCPeerConnection>
	): RTCPeerConnection {
		const pc = new Original(...args);
		try {
			pc.addEventListener('iceconnectionstatechange', () => {
				safeRecord('webrtc.pc', 'iceconnectionstatechange', {state: pc.iceConnectionState});
			});
			pc.addEventListener('connectionstatechange', () => {
				safeRecord('webrtc.pc', 'connectionstatechange', {state: pc.connectionState});
			});
			pc.addEventListener('icegatheringstatechange', () => {
				safeRecord('webrtc.pc', 'icegatheringstatechange', {state: pc.iceGatheringState});
			});
			pc.addEventListener('icecandidateerror', (event) => {
				const e = event as RTCPeerConnectionIceErrorEvent;
				safeRecord(
					'webrtc.pc',
					'icecandidateerror',
					{
						errorCode: e.errorCode,
						errorText: e.errorText,
						url: e.url,
						address: e.address,
						port: e.port,
					},
					'warn',
				);
			});
			pc.addEventListener('signalingstatechange', () => {
				safeRecord('webrtc.pc', 'signalingstatechange', {state: pc.signalingState});
			});
		} catch {}
		return pc;
	} as unknown as typeof RTCPeerConnection;

	Wrapped.prototype = Original.prototype;
	(Wrapped as unknown as {generateCertificate?: typeof RTCPeerConnection.generateCertificate}).generateCertificate =
		Original.generateCertificate?.bind(Original);
	window.RTCPeerConnection = Wrapped;
}

export function installRendererInstrumentation(): void {
	const flags = getGlobalFlags();
	if (flags[INSTALL_FLAG]) return;
	flags[INSTALL_FLAG] = true;

	try {
		safeRecord('app', 'instrumentation.install', {
			userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
			href: typeof location !== 'undefined' ? location.href : null,
			startedAt: new Date().toISOString(),
		});
	} catch {}

	installGlobalErrorHandlers();
	installConsoleHooks();
	installMediaDeviceWatcher();
	installGetUserMediaWrappers();
	installPeerConnectionWatcher();
}

export {hashDeviceId as hashDeviceIdForInstrumentation, summarizeArg as summarizeForInstrumentation};
